import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Response from backend when verifying a public candidate code
 */
export interface CandidateCodeVerificationResponse {
  valid: boolean;
  drive_id: string;
  candidate_id: string;
  id: string;
  ib_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class PublicService {
  baseUrl = environment.baseUrl;

  apiUrls = {
    verifyCandidateCode: '/api/v1/public/verify-candidate-code',
    verifyTenantToken: '/api/v1/public', // Base URL for tenant-based verification
  };

  // Public code state management for HTTP interceptor
  private publicCodeSubject = new BehaviorSubject<string | null>(null);
  public publicCode$ = this.publicCodeSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Verify the public code with backend
   * @param code - Public code to verify
   * @returns Observable with verification response
   */
  verifyCandidateCode(code: string): Observable<CandidateCodeVerificationResponse> {
    return this.http.get<CandidateCodeVerificationResponse>(
      `${this.baseUrl}${this.apiUrls.verifyCandidateCode}`,
      { params: { code } }
    );
  }

  /**
   * Verify token with tenant ID from URL path
   * @param token - Token to verify
   * @param tenantId - Tenant ID from URL path (e.g., 'iqigai')
   * @returns Observable with verification response
   */
  verifyTokenWithDynamicValue(token: string, tenantId: string): Observable<CandidateCodeVerificationResponse> {
    // Build URL with tenantId as path parameter: /api/v1/public/{tenantId}/candidate-view
    const url = `${this.baseUrl}${this.apiUrls.verifyTenantToken}/${tenantId}/candidate-view`;

    return this.http.get<CandidateCodeVerificationResponse>(url, {
      params: { token }
    });
  }

  /**
   * Universal verification method that handles both URL patterns
   * @param code - Token or code to verify
   * @param tenantId - Optional tenant ID from URL path
   * @returns Observable with verification response
   */
  verifyPublicAccess(code: string, tenantId?: string): Observable<CandidateCodeVerificationResponse> {
    if (tenantId) {
      // Use tenant-based token verification for dynamic URLs with fallback to traditional method
      return this.verifyTokenWithDynamicValue(code, tenantId).pipe(
        catchError((error) => {
          console.warn('Tenant token verification failed, falling back to traditional code verification:', error);
          // Fallback to traditional verification if the tenant endpoint doesn't exist
          return this.verifyCandidateCode(code);
        })
      );
    } else {
      // Use traditional code verification
      return this.verifyCandidateCode(code);
    }
  }

  /**
   * Set public code after successful verification
   * This code will be used by the HTTP interceptor
   * @param code - Verified public code
   */
  setPublicCode(code: string): void {
    this.publicCodeSubject.next(code);
  }

  /**
   * Get current public code (used by HTTP interceptor)
   * @returns Current public code or null
   */
  getCurrentPublicCode(): string | null {
    return this.publicCodeSubject.value;
  }

  /**
   * Clear public code on component destroy
   */
  clearPublicCode(): void {
    this.publicCodeSubject.next(null);
  }
}
